import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { forumCategories, allArticles } from '@/mocks/forumData';
import type { ForumArticle } from '@/mocks/forumData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const currentCategory = useMemo(
    () => forumCategories.find((c) => c.slug === slug) || forumCategories[0],
    [slug]
  );

  const articles: ForumArticle[] = useMemo(() => {
    const list = allArticles[currentCategory.slug] || [];
    return [...list].sort((a, b) => {
      if (sortBy === 'popular') return b.viewCount - a.viewCount;
      return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
    });
  }, [currentCategory.slug, sortBy]);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
      { '@type': 'ListItem', position: 2, name: '版块浏览', item: `${window.location.origin}/category/zonghe-jiaoliu` },
      { '@type': 'ListItem', position: 3, name: currentCategory.name },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main className="min-h-screen pt-16">
        {/* Header */}
        <section className="w-full bg-background-100 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                currentCategory.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                currentCategory.color === 'accent' ? 'bg-accent-100 text-accent-600' :
                'bg-secondary-100 text-secondary-600'
              }`}>
                <i className={`${currentCategory.icon} text-lg`}></i>
              </div>
              <div>
                <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500">{currentCategory.articleCount.toLocaleString()} 篇文章</p>
              </div>
            </div>
            <p className="text-sm text-foreground-600 max-w-2xl">{currentCategory.description}</p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
              {forumCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === currentCategory.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main Content */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-foreground-600">
                    共 <strong className="text-foreground-900">{articles.length}</strong> 篇文章
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSortBy('latest')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === 'latest'
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      最新
                    </button>
                    <button
                      onClick={() => setSortBy('popular')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === 'popular'
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      热门
                    </button>
                  </div>
                </div>

                {articles.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                      <i className="ri-inbox-line text-2xl"></i>
                    </div>
                    <p className="text-foreground-500">该版块暂无文章</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {articles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group flex flex-col sm:flex-row gap-4 bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                      >
                        <div className="flex-shrink-0 w-full sm:w-48 h-32 rounded-lg overflow-hidden">
                          <img
                            src={article.thumbnail}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded font-medium whitespace-nowrap">
                              {currentCategory.name}
                            </span>
                            {index === 0 && (
                              <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded font-medium whitespace-nowrap">
                                最新
                              </span>
                            )}
                          </div>
                          <h2 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                            {article.title}
                          </h2>
                          <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-foreground-500">
                            <span className="flex items-center gap-1">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-user-line text-xs"></i></span>
                              {article.author}
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-calendar-line text-xs"></i></span>
                              {article.publishDate}
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-eye-line text-xs"></i></span>
                              {article.viewCount.toLocaleString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-chat-3-line text-xs"></i></span>
                              {article.commentCount}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-64 flex-shrink-0">
                <div className="lg:sticky lg:top-28 space-y-5">
                  <div className="bg-background-100 border border-background-200/70 rounded-lg p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">热门标签</h3>
                    <div className="flex flex-wrap gap-2">
                      {['入口地址', '注册教程', '安全指南', '版块介绍', '使用教程', 'FAQ', '移动端', '社区规则'].map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-background-50 border border-background-200/70 rounded-full text-xs text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-background-100 border border-background-200/70 rounded-lg p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">其他版块</h3>
                    <div className="space-y-2">
                      {forumCategories
                        .filter((c) => c.slug !== currentCategory.slug)
                        .map((cat) => (
                          <Link
                            key={cat.slug}
                            to={`/category/${cat.slug}`}
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-background-50 transition-colors duration-200 cursor-pointer"
                          >
                            <div className={`w-7 h-7 flex items-center justify-center rounded-md ${
                              cat.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                              cat.color === 'accent' ? 'bg-accent-100 text-accent-600' :
                              'bg-secondary-100 text-secondary-600'
                            }`}>
                              <i className={`${cat.icon} text-xs`}></i>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-foreground-800 truncate">{cat.name}</p>
                              <p className="text-xs text-foreground-500">{cat.articleCount} 篇</p>
                            </div>
                          </Link>
                        ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}